import { useState } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { colors } from '../../styles/colors';

const labelStyle = {
  display: 'block',
  marginBottom: '0.5rem',
  fontSize: '0.875rem',
  fontWeight: '500',
  color: colors.primary.gray,
  fontFamily: '"Noto Sans", sans-serif',
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: `1px solid ${colors.secondary.lightGray}`,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  fontFamily: '"Noto Sans", sans-serif',
  boxSizing: 'border-box',
};

function UserFormModal({ isOpen, mode, formData, setFormData, consultants, onSubmit, onClose, submitting }) {
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: colors.text.white,
        borderRadius: '0.75rem',
        padding: '2rem',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
      }}>
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{
            fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: colors.primary.gray,
            margin: 0,
          }}>
            {mode === 'create' ? 'Créer un utilisateur' : "Modifier l'utilisateur"}
          </h2>
          <button
            onClick={onClose}
            style={{ padding: '0.5rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: colors.secondary.mediumGray, display: 'flex' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={onSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Email */}
            <div>
              <label style={labelStyle}>Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={inputStyle}
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label style={labelStyle}>
                Mot de passe {mode === 'create' ? '*' : '(laisser vide pour ne pas modifier)'}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required={mode === 'create'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  style={{ ...inputStyle, paddingRight: '3rem' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                    backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                    color: colors.secondary.mediumGray, display: 'flex', padding: '0.25rem',
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Nom */}
            <div>
              <label style={labelStyle}>Nom *</label>
              <input
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                style={inputStyle}
              />
            </div>

            {/* Prénom */}
            <div>
              <label style={labelStyle}>Prénom *</label>
              <input
                type="text"
                required
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                style={inputStyle}
              />
            </div>

            {/* Rôle */}
            <div>
              <label style={labelStyle}>Rôle *</label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="visiteur">Visiteur</option>
                <option value="consultant">Consultant</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            {/* Consultant associé */}
            <div>
              <label style={labelStyle}>Consultant associé</label>
              <select
                value={formData.consultant_id}
                onChange={(e) => setFormData({ ...formData, consultant_id: e.target.value })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Aucun consultant</option>
                {consultants.map(c => (
                  <option key={c.id} value={c.id}>{c.fields?.Nom || ''}</option>
                ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: colors.secondary.mediumGray, marginTop: '0.25rem', marginBottom: 0, fontFamily: '"Noto Sans", sans-serif' }}>
                Un consultant ne peut être associé qu'à un seul utilisateur
              </p>
            </div>

            {/* Actif */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="actif"
                checked={formData.actif}
                onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
              />
              <label htmlFor="actif" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer' }}>
                Compte actif
              </label>
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: colors.secondary.lightGray, color: colors.primary.gray,
                  border: 'none', borderRadius: '0.5rem',
                  fontSize: '1rem', fontWeight: '500', cursor: 'pointer',
                  fontFamily: '"Noto Sans", sans-serif', transition: 'all 200ms ease-in-out',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.secondary.mediumGray}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightGray}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  flex: 1, padding: '0.75rem',
                  backgroundColor: submitting ? colors.secondary.mediumGray : colors.primary.teal,
                  color: colors.text.white,
                  border: 'none', borderRadius: '0.5rem',
                  fontSize: '1rem', fontWeight: '500',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: '"Noto Sans", sans-serif', transition: 'all 200ms ease-in-out',
                }}
                onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = '#005f6a'; }}
                onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = colors.primary.teal; }}
              >
                {submitting ? 'Enregistrement...' : (mode === 'create' ? 'Créer' : 'Modifier')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserFormModal;
